<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_PROFIT')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="refInterestTable"
      :items="interestList"
      :busy="isBusy"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_PROFIT')"
              @click="onSelectInterestItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectInterestItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_PROFIT' : 'ACTIVATE_PROFIT')"
              @click="interestStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="interestRules">
        <b-form>
          <b-row class="match-height">
            <!-- Interest Name -->
            <b-col md="6">
              <InputField
                id="interest-name"
                v-model="interestFormData.name"
                :label="$t('Profit Name')"
                :placeholder="$t('Enter profit name')"
                type="text"
                rules="required"
                for="interest-name"
                :error-name="$t('Profit Name')"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Interest Name -->

            <!-- Type of Interest -->
            <b-col md="6">
              <SelectField
                id="type-of-interest"
                v-model="interestFormData.type"
                :label="$t('Type of Profit')"
                :placeholder="$t('Select')"
                :options="typeOptions"
                :reduce="option => option.value"
                :error-name="$t('Type of Profit')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :set-selected="setWithholdingTax"
              />
            </b-col>
          <!-- Type of Interest -->
          </b-row>

          <b-row class="match-height align-items-center">
            <!-- Default Rate -->
            <b-col md="6">
              <InputField
                id="default-rate"
                v-model.number="interestFormData.rate"
                :label="$t('Default Rate (%)')"
                :placeholder="$t('Enter default rate')"
                type="number"
                :error-name="$t('Default Rate')"
                rules="required|positive"
                for="default-rate"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Default Rate -->

            <!-- Apply withholding tax -->
            <b-col md="6">
              <b-form-group class="mt-1">
                <b-form-checkbox
                  v-model="interestFormData.applyWTH"
                  checked="false"
                  name="check-button"
                  switch
                  inline
                  :disabled="isDisableApplyWTH"
                >
                  {{ $t('Apply withholding tax?') }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <!-- Apply withholding tax -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchInterest"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Type -->
        <b-col md="6">
          <SelectField
            id="type"
            v-model="filter.type"
            :label="$t('Type')"
            :placeholder="$t('Select Type')"
            :reduce="option => option.value"
            :options="typeOptions"
            item="label"
          />
        </b-col>
        <!-- Type -->

        <!-- Code -->
        <b-col md="6">
          <InputField
            id="code"
            v-model="filter.code"
            :label="$t('Code')"
            :placeholder="$t('Enter code')"
            type="text"
          />
        </b-col>
        <!-- Code -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BDropdown, BDropdownItem, BModal,
  BFormGroup, BForm, BFormCheckbox, BBadge, BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { required, positive } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import { toDateWithYear } from '../../../helpers/formatDate'
import { interestType } from '../../../helpers/data'
import getStatusColor from '../../../helpers/getStatusColor'
import Button from '../../components/button/Button.vue'
import { accountPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BSpinner,
    BCol,
    BBadge,
    BTable,
    BDropdown,
    BDropdownItem,
    BModal,
    BFormGroup,
    BForm,
    BFormCheckbox,
    ValidationObserver,
    InputField,
    SelectField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'type',
      isReadonly: false,
      modalTitle: this.$t('Create New Profit'),
      buttonLabel: this.$t('Save'),
      isBusy: false,
      required,
      positive,
      isDisableApplyWTH: false,
      showSpinner: false,
      getStatusColor,
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'type', sortable: true, label: 'Type' },
        { key: 'rate', sortable: true, label: 'Rate (%)' },
        { key: 'code', sortable: true, label: 'Code' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      filter: {
        name: '',
        type: '',
        code: '',
        status: '',
        sortColumn: '',
      },
      interestFormData: {
        name: '',
        type: '',
        rate: '',
        applyWTH: false,
        status: '',
        id: '',
      },
      // typeOptions: interestType,
      interestList: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    typeOptions() {
      return interestType.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchInterest()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchInterest()
    },
  },
  mounted() {
    this.fetchInterest()
  },
  methods: {
    ...mapActions({
      getInterest: 'account/getInterestList',
      updateInterest: 'account/updateInterestAction',
      createInterest: 'account/createInterestAction',
      updateInterestStatus: 'account/updateStatus',
    }),
    setWithholdingTax(selectedValue, actionType) {
      if (selectedValue === 'Debit') {
        this.interestFormData.applyWTH = false
        this.isDisableApplyWTH = true
      } else if (selectedValue === 'Credit' && actionType === 'view') {
        this.isDisableApplyWTH = true
      } else {
        this.interestFormData.applyWTH = false
        this.isDisableApplyWTH = false
      }
    },
    async interestStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_PROFIT : accountPermissions.ACTIVATE_PROFIT

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateInterestStatus({
            statusValues, actionType, routeName: 'Interest', permission,
          })
          if (response.status === 200) {
            this.fetchInterest()
            const requestToText = this.$t('Your request to')
            const text = this.$t('Profit Management was successful')
            this.alertSuccess({ text: `${requestToText} ${action} ${text}` })
            this.showSpinner = false
          }
        } catch (error) {
          const requestToText = this.$t('Your request to')
          const reasonText = this.$t('Profit Management failed. Reason')
          this.alertError({ text: `${requestToText} ${action} ${reasonText}: ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const pdText = this.$t('this Profit Management')
      this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${pdText}?` })
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.interestRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const pdText = this.$t('this Profit Management')
          this.confirm(this.interestManagementAction, { text: `${areYouSure} ${this.buttonLabel} ${pdText}?` })
        }
      })
    },
    async interestManagementAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === this.$t('Save')
          ? await this.createInterest(this.interestFormData)
          : await this.updateInterest(this.interestFormData)

        const { status } = await response
        if (status === 200) {
          this.fetchInterest()
          this.hideModal('modal-lg')
          const requestToText = this.$t('Your request to')
          const text = this.$t('Profit Management was successful')
          this.alertSuccess({ text: `${requestToText} ${this.buttonLabel} ${text}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    onSelectInterestItem(items, activity, val) {
      this.interestFormData.name = items.name
      this.interestFormData.code = items.code
      this.interestFormData.rate = items.rate
      this.interestFormData.type = items.type
      this.interestFormData.id = items.id
      this.interestFormData.status = items.status
      this.setWithholdingTax(items.type, val)
      this.interestFormData.applyWTH = items.applyWTH
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Profit') : this.$t('Profit Management')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    resetModal() {
      this.interestFormData.name = ''
      this.interestFormData.type = ''
      this.interestFormData.rate = ''
      this.interestFormData.applyWTH = false
      this.isDisableApplyWTH = false
      this.isLoading = false
      this.buttonLabel = this.$t('Save')
      this.modalTitle = this.$t('Create Profit')
      this.isReadonly = false
    },
    async fetchInterest() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getInterest({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_PROFIT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.interestList = data.data
          this.totalCount = data.recordCount
        } else {
          this.interestList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchInterest()
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
